import { useCallback, useEffect, useRef, useState } from "react";
import { useShallow } from "zustand/react/shallow";
import type { Chart as KChart, DeepPartial, KLineData, NeighborData, Nullable, Period, Styles } from "klinecharts";
import { cn } from "@/lib/utils";
import { useChartStore } from "@/stores/chartStore";
import { useReplayStore } from "@/stores/replayStore";
import { useDrawingStore, type DrawingToolId } from "@/stores/drawingStore";
import { useSettingsStore } from "@/stores/settingsStore";
import { useSessionStore } from "@/stores/sessionStore";
import { useViewportManager } from "@/hooks/useViewportManager";
import { ChartSkeleton } from "@/components/lytick/ChartSkeleton";
import { ReplayBufferIndicator } from "@/components/lytick/ReplayBufferIndicator";
import { ObjectInspector } from "@/components/lytick/ObjectInspector";
import { ReplayContextMenu } from "@/components/lytick/ReplayContextMenu";
import { DrawingContextMenu } from "@/components/lytick/DrawingContextMenu";
import { type ChartTypeValue } from "@/lib/lytick/chart-config";
import { ChevronsRight } from "lucide-react";
import { registerCustomFibonacciOverlay } from "@/lib/lytick/fibonacci-overlay";
import { registerRiskRewardOverlay } from "@/lib/lytick/riskRewardOverlay";
import { registerReplayOverlays, REPLAY_POSITION_LINE_NAME, REPLAY_START_LINE_NAME } from "@/lib/lytick/replayOverlay";
import { registerCustomIndicators } from "@/lib/lytick/customIndicators";
import { readDesktopChartTheme } from "@/lib/themes";
import { registerEllipseOverlay } from "@/lib/lytick/ellipseOverlay";
import { registerTextOverlay } from "@/lib/lytick/textOverlay";
import { TextAnnotationEditor } from "@/components/lytick/TextAnnotationEditor";
import {
  createDrawing,
  cancelPendingDrawings,
  deserializeOverlays,
  destroyOverlayBridge,
  initOverlayBridge,
  serializeOverlays,
} from "@/lib/lytick/overlay-bridge";
import { getCurrentCsvHash, saveDrawingsForHash } from "@/lib/lytick/drawing-persistence";
import {
  disposeChartRuntime,
  getOrCreateChartRuntime,
} from "@/lib/lytick/chart-runtime";
import { buildAxisTicks, getTimeframeAxisFlags } from "@/lib/lytick/time-axis";
import { ChartTemplateSaveDialog } from "@/components/features/ChartTemplateSaveDialog";
import { DrawingWarningDialog } from "@/components/features/DrawingWarningDialog";

import { OVERLAY_NAMES, getCatalogEntry, buildIndicatorStyles } from "@/lib/lytick/indicatorRegistry";
import type { IndicatorInstance } from "@/types/indicator";

function formatRestartDate(epochMs: number): string {
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  }).format(epochMs);
}


function hexToRgba(hex: string, opacity: number) {
  const sanitized = hex.replace("#", "");
  const parsed = Number.parseInt(sanitized, 16);

  if (!Number.isFinite(parsed)) {
    return `rgba(196, 136, 11, ${opacity})`; // mirrors --color-fib-level (tokens.css) — canvas fallback, intentional
  }

  const r = (parsed >> 16) & 255;
  const g = (parsed >> 8) & 255;
  const b = parsed & 255;

  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

function timeFrameToPeriod(tf: string): Period {
  const match = tf.match(/^(\d+)\s*(mn|mo|w|m|h|d|t)$/i);
  if (match) {
    const num = parseInt(match[1], 10);
    const unit = match[2].toLowerCase();
    switch (unit) {
      case "t":
        return { type: "second", span: 1 };
      case "m":
        return { type: "minute", span: num };
      case "h":
        return { type: "hour", span: num };
      case "d":
        return { type: "day", span: num };
      case "w":
        return { type: "week", span: num };
      case "mn":
      case "mo":
        return { type: "month", span: num };
    }
  }

  return { type: "minute", span: 1 };
}


function resolveDesktopCandleType(
  chartType: string,
): "candle_solid" | "candle_stroke" | "area" | "ohlc" {
  const ct = chartType as ChartTypeValue;
  switch (ct) {
    case "candle_solid":
      return "candle_solid";
    case "candle_stroke":
      return "candle_stroke";
    case "candle_heikin_ashi":
      return "candle_solid";
    case "area":
      return "area";
    case "line":
      return "area"; // Line is area with transparent fill
    case "ohlc":
      return "ohlc";
    default:
      return "candle_solid";
  }
}

function buildStyles(
  chartType: string,
  desktopMode: boolean,
  showOHLC: boolean,
): DeepPartial<Styles> {
  const theme = readDesktopChartTheme();

  if (!desktopMode) {
    return {
      grid: {
        show: true,
        horizontal: {
          show: true,
          style: "dashed",
          dashedValue: [2, 2],
          size: 1,
          color: theme.chartGrid,
        },
        vertical: {
          show: true,
          style: "dashed",
          dashedValue: [2, 2],
          size: 1,
          color: theme.chartGrid,
        },
      },
      candle: {
        type: resolveDesktopCandleType(chartType),
        bar: {
          upColor:             theme.bullish,
          downColor:           theme.bearish,
          noChangeColor:       theme.textSecondary,
          upBorderColor:       theme.bullish,
          downBorderColor:     theme.bearish,
          noChangeBorderColor: theme.textSecondary,
          upWickColor:         theme.bullish,
          downWickColor:       theme.bearish,
          noChangeWickColor:   theme.textSecondary,
        },
        priceMark: {
          show: true,
          high: { show: showOHLC, color: theme.textSecondary, textOffset: 5, textSize: 10, textFamily: "monospace", textWeight: "normal" },
          low:  { show: showOHLC, color: theme.textSecondary, textOffset: 5, textSize: 10, textFamily: "monospace", textWeight: "normal" },
          last: {
            show: true,
            upColor:       theme.bullish,
            downColor:     theme.bearish,
            noChangeColor: theme.textPrimary,
            line: { show: true, style: "dashed", dashedValue: [4, 4], size: 1 },
            text: {
              show: true,
              style: "fill",
              color: theme.textPrimary,
              size: 11,
              family: "monospace",
              weight: "normal",
              borderColor: "transparent",
              borderRadius: 2,
            },
          },
        },
        tooltip: {
          showRule: "follow_cross",
          showType: "standard",
          title: { show: false },
          legend: {
            template: (data: NeighborData<Nullable<KLineData>>) => {
              const kLineData = data.current;
              if (kLineData) {
                useChartStore.getState().setOHLC({
                  open: kLineData.open,
                  high: kLineData.high,
                  low: kLineData.low,
                  close: kLineData.close,
                });
              }
              return [];
            },
          },
        },
        area: {
          lineSize: chartType === 'line' ? 1.5 : 1.6,
          lineColor: theme.accent,
          value: "close",
          smooth: true,
          backgroundColor: chartType === 'line'
            ? [{ offset: 0, color: 'transparent' }, { offset: 1, color: 'transparent' }]
            : [
                { offset: 0, color: theme.chartAreaFill },
                { offset: 1, color: theme.chartAreaStroke },
              ],
          point: {
            color: theme.accent,
            radius: chartType === 'line' ? 0 : 4,
            rippleColor: theme.chartAreaStroke,
            rippleRadius: chartType === 'line' ? 0 : 6,
            animation: false,
          },
        },
      },
      separator: {
        size: 3,
        color: "rgba(255,255,255,0.22)",
        fill: true,
        activeBackgroundColor: hexToRgba(theme.accent, 0.15),
      },
      xAxis: {
        show: true,
        axisLine: { show: false },
        tickLine: { show: false },
        tickText: { show: true, color: theme.textSecondary, size: 10, family: "monospace", weight: "normal", marginStart: 4, marginEnd: 4 },
      },
    };
  }

  return {
    grid: {
      show: true,
      horizontal: { show: true, color: theme.border, size: 1, style: "dashed" },
      vertical: { show: true, color: theme.border, size: 1, style: "dashed" },
    },
    candle: {
      type: resolveDesktopCandleType(chartType),
      bar: {
        upColor: theme.bullish,
        downColor: theme.bearish,
        noChangeColor: theme.textSecondary,
        upBorderColor: theme.bullish,
        downBorderColor: theme.bearish,
        noChangeBorderColor: theme.textSecondary,
        upWickColor: theme.bullish,
        downWickColor: theme.bearish,
        noChangeWickColor: theme.textSecondary,
      },
      priceMark: {
        show: true,
        high: {
          show: showOHLC,
          color: theme.textSecondary,
          textOffset: 5,
          textSize: 10,
          textFamily: "IBM Plex Mono, monospace",
          textWeight: "normal",
        },
        low: {
          show: showOHLC,
          color: theme.textSecondary,
          textOffset: 5,
          textSize: 10,
          textFamily: "IBM Plex Mono, monospace",
          textWeight: "normal",
        },
        last: {
          show: true,
          upColor: theme.bullish,
          downColor: theme.bearish,
          noChangeColor: theme.textSecondary,
          line: { show: true, style: "dashed", dashedValue: [4, 4], size: 1 },
          text: {
            show: true,
            style: "fill",
            size: 11,
            family: "IBM Plex Mono, monospace",
            weight: "normal",
            color: theme.textPrimary,
            borderColor: "transparent",
          },
        },
      },
      tooltip: {
        showRule: "follow_cross",
        showType: "standard",
        title: { show: false },
        legend: {
                    template: (data: NeighborData<Nullable<KLineData>>) => {
            const kLineData = data.current;
            if (kLineData) {
              useChartStore.getState().setOHLC({
                open: kLineData.open,
                high: kLineData.high,
                low: kLineData.low,
                close: kLineData.close,
              });
            }
            return [];
          },
        },
      },
      area: {
        lineSize: chartType === 'line' ? 1.5 : 1.6,
        lineColor: theme.accent,
        value: "close",
        smooth: true,
        backgroundColor: chartType === 'line'
          ? [{ offset: 0, color: 'transparent' }, { offset: 1, color: 'transparent' }]
          : [
              { offset: 0, color: hexToRgba(theme.accent, 0.22) },
              { offset: 1, color: hexToRgba(theme.accent, 0.02) },
            ],
        point: {
          color: theme.accent,
          radius: chartType === 'line' ? 0 : 4,
          rippleColor: hexToRgba(theme.accent, 0.2),
          rippleRadius: chartType === 'line' ? 0 : 6,
          animation: false,
        },
      },
    },
    separator: {
      size: 2,
      color: "rgba(255,255,255,0.22)",
      fill: true,
      activeBackgroundColor: hexToRgba(theme.accent, 0.15),
    },
    xAxis: {
      show: true,
      axisLine: { show: false },
      tickLine: { show: false },
      tickText: { show: true, color: theme.textSecondary, size: 11, family: "IBM Plex Mono, monospace", weight: "normal", marginStart: 6, marginEnd: 6 },
    },
    crosshair: {
      show: true,
      horizontal: {
        show: true,
        line: { show: true, color: hexToRgba(theme.accent, 0.3), style: "dashed", size: 1 },
        text: { show: true, color: theme.textPrimary, size: 10, family: "IBM Plex Mono", backgroundColor: theme.elevated },
      },
      vertical: {
        show: true,
        line: { show: true, color: hexToRgba(theme.accent, 0.3), style: "dashed", size: 1 },
        text: {
          show: true,
          color: theme.textPrimary,
          size: 11,
          family: "IBM Plex Mono",
          weight: "500",
          backgroundColor: theme.overlay,
          borderColor: hexToRgba(theme.accent, 0.5),
          borderSize: 1,
          paddingLeft: 6,
          paddingRight: 6,
          paddingTop: 3,
          paddingBottom: 3,
          borderRadius: 4,
        },
      },
    },
  };
}

let rectangleRegistered = false;

async function registerRectangleOverlay() {
  if (rectangleRegistered) return;
  rectangleRegistered = true;

  try {
    const klinecharts = await import("klinecharts");
    klinecharts.registerOverlay({
      name: "rectangle",
      totalStep: 3,
      needDefaultPointFigure: true,
      needDefaultXAxisFigure: false,
      needDefaultYAxisFigure: false,
      styles: {
        polygon: { color: "transparent" },
        line: { color: "var(--color-fib-level)", size: 1 },
      },
      createPointFigures: ({ coordinates }: { coordinates: Array<{ x: number; y: number }> }) => {
        if (coordinates.length < 2) return [];
        const x1 = Math.min(coordinates[0].x, coordinates[1].x);
        const y1 = Math.min(coordinates[0].y, coordinates[1].y);
        const x2 = Math.max(coordinates[0].x, coordinates[1].x);
        const y2 = Math.max(coordinates[0].y, coordinates[1].y);
        return [{
          type: "rect",
          attrs: { x: x1, y: y1, width: x2 - x1, height: y2 - y1 },
          styles: {
            style: "stroke",
            color: "transparent",
            borderColor: "var(--color-fib-level)",
            borderSize: 1,
            borderStyle: "solid",
          },
        }];
      },
    });
  } catch {
    // Ignore SSR and duplicate registration issues.
  }
}

interface ChartAreaProps {
  desktopMode?: boolean;
  chartTypeOverride?: string;
  timeframeOverride?: string;
  interactive?: boolean;
  chartId?: string;
  onChartInit?: (chart: any) => void;
}

export function ChartArea({
  desktopMode = false,
  chartTypeOverride,
  timeframeOverride,
  interactive = false,
  chartId = "chart",
  onChartInit,
}: ChartAreaProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<KChart | null>(null);
  const runtimeRef = useRef<ReturnType<typeof getOrCreateChartRuntime> | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const activePanesRef = useRef<Map<string, string>>(new Map());
  // Tracks the serialized figureStyles last applied to each indicator instance
  // so we can detect changes without re-reading the KlineCharts internal state.
  const appliedStylesRef = useRef<Map<string, string>>(new Map());
  const drawingsRestoredRef = useRef(false);
  const touchGestureRef = useRef({ touchCount: 0, moved: false, startX: 0, startY: 0 });
  const MOBILE_TAP_MOVE_THRESHOLD = 10;

  const {
    symbol,
    timeframe,
    chartType,
    indicators,
    setChartType,
    hasData,
    livePrice,
    oldestEpoch,
  } = useChartStore(
    useShallow((state) => ({
      symbol: state.symbol,
      timeframe: state.timeframe,
      chartType: state.chartType,
      indicators: state.indicators,
      setChartType: state.setChartType,
      hasData: state.hasData,
      livePrice: state.livePrice,
      oldestEpoch: state.oldestEpoch,
    })),
  );
  const {
    isActive: isReplay,
    isReplayMode,
    isStartLineLocked,
    startLineTimestamp,
    position,
    anchorIndex: replayAnchorIndex,
    sessionState: replaySessionState,
    masterColumnsVersion: replayMasterColumnsVersion,
    sourceGranularitySeconds: replaySourceGranularitySeconds,
    contextCandles: replayContextCandles,
    contextGranularitySeconds: replayContextGranularitySeconds,
    cursorTimestamp,
    blindMode,
  } = useReplayStore(
    useShallow((state) => ({
      isActive: state.isActive,
      isReplayMode: state.isReplayMode,
      isStartLineLocked: state.isStartLineLocked,
      startLineTimestamp: state.startLineTimestamp,
      position: state.position,
      anchorIndex: state.anchorIndex,
      sessionState: state.sessionState,
      masterColumnsVersion: state.masterColumnsVersion,
      sourceGranularitySeconds: state.sourceGranularitySeconds,
      contextCandles: state.contextCandles,
      contextGranularitySeconds: state.contextGranularitySeconds,
      cursorTimestamp: state.cursorTimestamp,
      blindMode: state.blindMode,
    })),
  );

  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [drawingWarning, setDrawingWarning] = useState<{ targetType: string } | null>(null);
  const drawings = useDrawingStore((s) => s.drawings);

  const { activeTool, setActiveTool } = useDrawingStore(
    useShallow((state) => ({
      activeTool: state.activeTool,
      setActiveTool: state.setActiveTool,
    })),
  );
  const { showOHLC, chartAppearance } = useSettingsStore(
    useShallow((state) => ({
      showOHLC: state.showOHLC,
      chartAppearance: state.chartAppearance,
    })),
  );
  const timezone = chartAppearance.timezone;
  const activeChartType = chartTypeOverride ?? chartType;
  const activeTimeframe = timeframeOverride ?? timeframe;
  const isPrimaryChart = chartId !== "secondary";
  const activeTimeframeRef = useRef(activeTimeframe);
  useEffect(() => {
    activeTimeframeRef.current = activeTimeframe;
  }, [activeTimeframe]);

  const timezoneRef = useRef(timezone);
  useEffect(() => {
    timezoneRef.current = timezone;
  }, [timezone]);

  const blindModeRef = useRef(blindMode);
  useEffect(() => {
    blindModeRef.current = blindMode;
  }, [blindMode]);

  const cursorTimestampRef = useRef(cursorTimestamp);
  useEffect(() => {
    cursorTimestampRef.current = cursorTimestamp;
  }, [cursorTimestamp]);



  const [noDataBoundaryPx, setNoDataBoundaryPx] = useState<number | null>(null);
  const [blindCurtainPx, setBlindCurtainPx] = useState<number | null>(null);
  const [isNearStartLine, setIsNearStartLine] = useState(false);
  const [isDraggingStartLine, setIsDraggingStartLine] = useState(false);
  const isDraggingStartLineRef = useRef(false);
  const [isNearPositionLine, setIsNearPositionLine] = useState(false);
  const [isDraggingPositionLine, setIsDraggingPositionLine] = useState(false);
  const isDraggingPositionLineRef = useRef(false);
  const [posLineRestartPrompt, setPosLineRestartPrompt] = useState<number | null>(null);
  const preDragCursorRef = useRef<number | null>(null);

  // Ctrl+S → open template save dialog
  useEffect(() => {
    if (!interactive || !isPrimaryChart) return;
    const handler = () => setTemplateDialogOpen(true);
    window.addEventListener("lytick:save-chart-template", handler);
    return () => window.removeEventListener("lytick:save-chart-template", handler);
  }, [interactive, isPrimaryChart]);

  // Auto-dismiss restart prompt after 7 seconds (cancel: snap back to pre-drag position)
  useEffect(() => {
    if (posLineRestartPrompt === null) return;
    const timer = setTimeout(() => {
      if (preDragCursorRef.current !== null) {
        useReplayStore.getState().seekToTimestamp(preDragCursorRef.current);
      }
      setPosLineRestartPrompt(null);
    }, 7000);
    return () => clearTimeout(timer);
  }, [posLineRestartPrompt]);

  // Transient zoom badge ("N candles") shown after onZoomEnd (F-009)
  const [zoomBadge, setZoomBadge] = useState<string | null>(null);
  const zoomBadgeTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  // Track mouse proximity to start line for grab/grabbing cursor
  useEffect(() => {
    const chart = chartRef.current;
    const container = containerRef.current;
    if (!chart || !container || !isReplayMode || !isStartLineLocked || !isPrimaryChart || !interactive) {
      setIsNearStartLine(false);
      setIsDraggingStartLine(false);
      return;
    }

    const PROXIMITY_PX = 6;

    const getStartLinePixelX = (): number | null => {
      try {
        const overlays = chart.getOverlays({ name: REPLAY_START_LINE_NAME });
        if (overlays.length > 0 && (overlays[0] as any).points?.length > 0) {
          const ts = (overlays[0] as any).points[0].timestamp;
          if (ts != null) {
            const result = chart.convertToPixel(
              [{ timestamp: ts }],
              { paneId: "candle_pane" },
            ) as Array<{ x?: number }> | undefined;
            return Array.isArray(result) ? result[0]?.x ?? null : null;
          }
        }
      } catch {}
      return null;
    };

    const handleMouseMove = (e: MouseEvent) => {
      const lineX = getStartLinePixelX();
      if (lineX == null) {
        setIsNearStartLine(false);
        return;
      }
      const rect = container.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      setIsNearStartLine(Math.abs(mouseX - lineX) <= PROXIMITY_PX);
    };

    const handleMouseLeave = () => {
      setIsNearStartLine(false);
    };

    container.addEventListener("mousemove", handleMouseMove);
    container.addEventListener("mouseleave", handleMouseLeave);

    return () => {
      container.removeEventListener("mousemove", handleMouseMove);
      container.removeEventListener("mouseleave", handleMouseLeave);
      setIsNearStartLine(false);
    };
  }, [isReplayMode, isStartLineLocked, isPrimaryChart, interactive, startLineTimestamp]);

  // Track mouse proximity to position line for grab/grabbing cursor during active replay
  useEffect(() => {
    const chart = chartRef.current;
    const container = containerRef.current;
    if (!chart || !container || !isReplay || !isPrimaryChart || !interactive) {
      setIsNearPositionLine(false);
      return;
    }

    const PROXIMITY_PX = 6;

    const getPositionLinePixelX = (): number | null => {
      try {
        const overlays = chart.getOverlays({ name: REPLAY_POSITION_LINE_NAME });
        if (overlays.length > 0 && (overlays[0] as any).points?.length > 0) {
          const ts = (overlays[0] as any).points[0].timestamp;
          if (ts != null) {
            const result = chart.convertToPixel(
              [{ timestamp: ts }],
              { paneId: "candle_pane" },
            ) as Array<{ x?: number }> | undefined;
            return Array.isArray(result) ? result[0]?.x ?? null : null;
          }
        }
      } catch {}
      return null;
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (isDraggingPositionLine) return;
      const lineX = getPositionLinePixelX();
      if (lineX == null) { setIsNearPositionLine(false); return; }
      const rect = container.getBoundingClientRect();
      setIsNearPositionLine(Math.abs(e.clientX - rect.left - lineX) <= PROXIMITY_PX);
    };

    const handleMouseLeave = () => setIsNearPositionLine(false);

    container.addEventListener("mousemove", handleMouseMove);
    container.addEventListener("mouseleave", handleMouseLeave);
    return () => {
      container.removeEventListener("mousemove", handleMouseMove);
      container.removeEventListener("mouseleave", handleMouseLeave);
      setIsNearPositionLine(false);
    };
  }, [isReplay, isPrimaryChart, interactive, cursorTimestamp, isDraggingPositionLine]);

  // Compute pixel boundary for the no-data zone overlay
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart || !isReplayMode || isStartLineLocked || !isPrimaryChart || !interactive) {
      setNoDataBoundaryPx(null);
      return;
    }
    if (oldestEpoch == null) {
      setNoDataBoundaryPx(null);
      return;
    }

    const computeBoundary = () => {
      try {
        const result = chart.convertToPixel(
          [{ timestamp: oldestEpoch }],
          { paneId: "candle_pane" },
        ) as Array<{ x?: number }> | undefined;
        const px = Array.isArray(result) ? result[0]?.x ?? null : null;
        setNoDataBoundaryPx(px != null && px > 0 ? px : null);
      } catch {
        setNoDataBoundaryPx(null);
      }
    };

    computeBoundary();

    // Recompute on scroll/zoom
    const onAction = () => computeBoundary();
    (chart as any).subscribeAction("onScrollEnd", onAction);
    (chart as any).subscribeAction("onZoomEnd", onAction);

    const onPaneDrag = () => {
      const panes = (chart as any).getPaneOptions();
      if (Array.isArray(panes)) {
        panes.forEach((p: any) => {
          if (p.id && p.height) {
            useChartStore.getState().setPaneHeight(p.id, p.height);
            useSettingsStore.getState().setPaneHeight(p.id, p.height);
          }
        });
      }
    };
    (chart as any).subscribeAction("onPaneDrag", onPaneDrag);

    return () => {
      (chart as any).unsubscribeAction("onScrollEnd", onAction);
      (chart as any).unsubscribeAction("onZoomEnd", onAction);
      (chart as any).unsubscribeAction("onPaneDrag", onPaneDrag);
    };
  }, [isReplayMode, isStartLineLocked, isPrimaryChart, interactive, oldestEpoch]);

  // Blind mode curtain — pixel position of the replay cursor
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart || !isReplay || !blindMode || cursorTimestamp == null) {
      setBlindCurtainPx(null);
      return;
    }

    const computeCurtain = () => {
      try {
        const result = chart.convertToPixel(
          [{ timestamp: cursorTimestamp }],
          { paneId: "candle_pane" },
        ) as Array<{ x?: number }> | undefined;
        const px = Array.isArray(result) ? result[0]?.x ?? null : null;
        setBlindCurtainPx(px != null ? px : null);
      } catch {
        setBlindCurtainPx(null);
      }
    };

    computeCurtain();
    (chart as any).subscribeAction("onScrollEnd", computeCurtain);
    (chart as any).subscribeAction("onZoomEnd", computeCurtain);

    return () => {
      (chart as any).unsubscribeAction("onScrollEnd", computeCurtain);
      (chart as any).unsubscribeAction("onZoomEnd", computeCurtain);
    };
  }, [isReplay, blindMode, cursorTimestamp]);

  const { isUserScrolled, jumpToLive } = useViewportManager({
    chartRef,
    enabled: isPrimaryChart,
    isReplay,
    livePrice,
    symbol,
  });

  const syncIndicators = useCallback((chart: KChart, instances: IndicatorInstance[]) => {
    const paneMap = activePanesRef.current;
    const stylesMap = appliedStylesRef.current;
    const current = chart.getIndicators();

    // Build set of our managed indicator IDs currently on the chart
    const activeIds = new Set(instances.map((i) => i.id));

    // Remove indicators that are no longer in Zustand
    for (const ki of current) {
      // Only remove indicators we created (tracked in paneMap)
      if (!activeIds.has(ki.id) && paneMap.has(ki.id)) {
        chart.removeIndicator({ id: ki.id, paneId: ki.paneId });
        paneMap.delete(ki.id);
        stylesMap.delete(ki.id);
      }
    }

    // Create new or update existing indicators
    for (const inst of instances) {
      const existing = current.find((ki) => ki.id === inst.id);
      const isOverlay = OVERLAY_NAMES.has(inst.name);
      const catalog = getCatalogEntry(inst.name);
      const styles = catalog ? buildIndicatorStyles(inst.figureStyles, catalog) : undefined;
      const stylesJson = JSON.stringify(inst.figureStyles);

      if (!existing) {
        const indicatorOptions: any = {
          name: inst.name,
          id: inst.id,
          calcParams: inst.calcParams,
          visible: inst.visible,
          styles,
        };
        if (inst.source) {
          indicatorOptions.source = inst.source;
        }
        const indicatorId = chart.createIndicator(
          indicatorOptions,
          isOverlay,
          isOverlay
            ? { id: "candle_pane" }
            : {
                id: `pane_${inst.id}`,
                dragEnabled: true,
                minHeight: 60,
                height: useSettingsStore.getState().paneHeights[`pane_${inst.id}`]
                    ?? useChartStore.getState().paneHeights[`pane_${inst.id}`],
              },
        );
        if (indicatorId) {
          paneMap.set(inst.id, indicatorId);
          stylesMap.set(inst.id, stylesJson);
        }
      } else if (existing.paneId) {
        // Indicator exists — override if params, source, visibility, or styles changed
        const paramsChanged =
          JSON.stringify(existing.calcParams) !== JSON.stringify(inst.calcParams);
        const sourceChanged = (existing as any).source !== inst.source;
        const visibilityChanged = existing.visible !== inst.visible;
        const figureStylesChanged = stylesMap.get(inst.id) !== stylesJson;

        if (paramsChanged || sourceChanged || visibilityChanged || figureStylesChanged) {
          const overrideOptions: any = {
            id: inst.id,
            name: inst.name,
            paneId: existing.paneId,
            calcParams: inst.calcParams,
            visible: inst.visible,
            styles,
          };
          if (inst.source) {
            overrideOptions.source = inst.source;
          }
          chart.overrideIndicator(overrideOptions);
          stylesMap.set(inst.id, stylesJson);
        }
        paneMap.set(inst.id, existing.paneId);
      }
    }
  }, []);

  useEffect(() => {
    if (!interactive || !activeTool) return;
    createDrawing(activeTool, useDrawingStore.getState().drawingColor);
  }, [activeTool, interactive]);

  useEffect(() => {
    if (!containerRef.current) return;

    let mounted = true;
    let onTouchStart: ((event: TouchEvent) => void) | null = null;
    let onTouchMove: ((event: TouchEvent) => void) | null = null;
    let onContextMenuIntercept: ((event: MouseEvent) => void) | null = null;
    let onMouseDownIntercept: ((event: MouseEvent) => void) | null = null;
    let onDblClick: ((event: MouseEvent) => void) | null = null;

    const boot = async () => {
      const [klinecharts] = await Promise.all([
        import("klinecharts"),
        registerRectangleOverlay(),
        registerCustomFibonacciOverlay(),
        registerRiskRewardOverlay(),
        registerReplayOverlays(),
        registerEllipseOverlay(),
        registerTextOverlay(),
      ]);
      registerCustomIndicators();
      if (!mounted || !containerRef.current) return;

      const chart = klinecharts.init(containerRef.current, {
        styles: buildStyles(activeChartType, desktopMode, showOHLC),
        timezone: timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
        layout: [
          { type: "candle" },
          {
            type: "xAxis",
            options: {
              axis: {
                createTicks: (params) => {
                  const periodType = timeFrameToPeriod(activeTimeframeRef.current).type;
                  const flags = getTimeframeAxisFlags(periodType);
                  const barSpacing = params.bounding.width / Math.max(1, params.range.range);
                  return buildAxisTicks(params.defaultTicks, {
                    barSpacing,
                    flags,
                    tz: timezoneRef.current || undefined,
                    chartWidth: params.bounding.width,
                  });
                },
              },
            },
          },
        ],
      });

      if (!chart) return;
      chartRef.current = chart;
      if (onChartInit) onChartInit(chart);

      // Prevent sub-panes from squeezing the main candle view below a usable height
      try {
        chart.setPaneOptions({ id: 'candle_pane', minHeight: 150 });
      } catch {
        // ignore if KLineCharts version does not support minHeight on the main pane
      }

      // Date formatter for X axis
      chart.setFormatter({
        formatDate: (params) => params.dateTimeFormat.format(params.timestamp),
      });

      const runtime = getOrCreateChartRuntime(chartId);
      runtimeRef.current = runtime;
      runtime.attachChart(chart);
      runtime.applyAppearance(chartAppearance);
      runtime.setView({
        symbol,
        timeframe: activeTimeframe,
        chartType: activeChartType,
      });
      runtime.setReplayState({
        isReplay,
        position,
        sessionState: replaySessionState,
        masterColumnsVersion: replayMasterColumnsVersion,
        sourceGranularitySeconds: replaySourceGranularitySeconds,
        contextCandles: replayContextCandles,
        contextGranularitySeconds: replayContextGranularitySeconds,
      });

      chart.setZoomEnabled(true);
      chart.setZoomAnchor({ main: "cursor", xAxis: "cursor" });
      chart.setScrollEnabled(true);
      chart.setSymbol({ ticker: symbol, pricePrecision: 2 });
      chart.setPeriod(timeFrameToPeriod(activeTimeframe));

      chart.setDataLoader({
        getBars: async ({ callback, type }) => {
          const result = await runtime.getBars(type);
          callback(result.data, result.more);
        },
      });

      if (interactive) {
        initOverlayBridge(chart);
        if (typeof window !== "undefined") {
          (window as unknown as Record<string, unknown>).__LYTICK_CHART__ = chart;
        }
      }

      const ro = new ResizeObserver(() => {
        try {
          chart.resize();
        } catch {
          // Ignore resize after disposal.
        }
      });
      ro.observe(containerRef.current);
      resizeObserverRef.current = ro;

      syncIndicators(chart, interactive ? indicators : []);

      onTouchStart = (event: TouchEvent) => {
        if (useDrawingStore.getState().activeTool && event.cancelable) {
          event.preventDefault();
        }
        if (event.touches.length === 2) {
          if (useDrawingStore.getState().activeTool) {
            cancelPendingDrawings();
            useDrawingStore.getState().setActiveTool(null);
          }
          touchGestureRef.current = { touchCount: 0, moved: true, startX: 0, startY: 0 };
          return;
        }
        if (event.touches.length !== 1) {
          touchGestureRef.current = {
            touchCount: event.touches.length,
            moved: true,
            startX: 0,
            startY: 0,
          };
          return;
        }
        const touch = event.touches[0];
        touchGestureRef.current = {
          touchCount: 1,
          moved: false,
          startX: Number(touch.clientX),
          startY: Number(touch.clientY),
        };
      };

      onTouchMove = (event: TouchEvent) => {
        if (useDrawingStore.getState().activeTool && event.cancelable) {
          event.preventDefault();
        }
        if (touchGestureRef.current.touchCount !== 1 || event.touches.length !== 1) {
          touchGestureRef.current.moved = true;
          return;
        }
        const touch = event.touches[0];
        const deltaX = Math.abs(Number(touch.clientX) - touchGestureRef.current.startX);
        const deltaY = Math.abs(Number(touch.clientY) - touchGestureRef.current.startY);
        if (deltaX > MOBILE_TAP_MOVE_THRESHOLD || deltaY > MOBILE_TAP_MOVE_THRESHOLD) {
          touchGestureRef.current.moved = true;
        }
      };

      if (interactive && onTouchStart && onTouchMove) {
        containerRef.current.addEventListener("touchstart", onTouchStart, { passive: false });
        containerRef.current.addEventListener("touchmove", onTouchMove, { passive: false });
      }

      onContextMenuIntercept = (event: MouseEvent) => {
        if (useDrawingStore.getState().activeTool) {
          event.stopPropagation();
          event.preventDefault();
        }
      };

      onMouseDownIntercept = (event: MouseEvent) => {
        if (event.button === 2 && useDrawingStore.getState().activeTool) {
          event.stopPropagation();
        }
      };

      onDblClick = null;
      if (interactive && onContextMenuIntercept && onMouseDownIntercept) {
        containerRef.current.addEventListener("contextmenu", onContextMenuIntercept, { capture: true });
        containerRef.current.addEventListener("mousedown", onMouseDownIntercept, { capture: true });
        containerRef.current.addEventListener("mouseup", onMouseDownIntercept, { capture: true });
      }
      if (interactive) {
        onDblClick = () => {
          const store = useDrawingStore.getState();
          const selectedIds = store.selectedDrawingIds;
          if (selectedIds.length === 1) {
            const selectedId = selectedIds[0];
            const drawing = store.drawings.find((d) => d.id === selectedId);
            if (drawing && (drawing.toolId === "textAnnotation" || drawing.toolId === "textTag")) {
              store.setEditingTextDrawingId(selectedId);
            }
          }
        };
        containerRef.current.addEventListener("dblclick", onDblClick);
      }
    };

    boot();

    return () => {
      mounted = false;
      if (interactive) {
        destroyOverlayBridge();
          if (typeof window !== "undefined") {
            (window as unknown as Record<string, unknown>).__LYTICK_CHART__ = undefined;
          }
      }
      if (interactive && containerRef.current && onTouchStart && onTouchMove) {
        containerRef.current.removeEventListener("touchstart", onTouchStart);
        containerRef.current.removeEventListener("touchmove", onTouchMove);
      }
      if (interactive && containerRef.current && onContextMenuIntercept && onMouseDownIntercept) {
        containerRef.current.removeEventListener("contextmenu", onContextMenuIntercept, { capture: true });
        containerRef.current.removeEventListener("mousedown", onMouseDownIntercept, { capture: true });
        containerRef.current.removeEventListener("mouseup", onMouseDownIntercept, { capture: true });
      }
      if (interactive && containerRef.current && onDblClick) {
        containerRef.current.removeEventListener("dblclick", onDblClick);
      }
      resizeObserverRef.current?.disconnect();
      resizeObserverRef.current = null;
      const chart = chartRef.current;
      if (chart) {
        import("klinecharts").then((module) => {
          try {
            module.dispose(chart);
          } catch {
            // Ignore disposal after teardown.
          }
        });
      }
      chartRef.current = null;
      disposeChartRuntime(chartId);
      runtimeRef.current = null;
      activePanesRef.current.clear();
    };
  }, [chartId, desktopMode, interactive, syncIndicators]);

  // Single source of truth for chart styles (including crosshair and candle convention)
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;
    const styles = buildStyles(activeChartType, desktopMode, showOHLC);

    if (chartAppearance.candleConvention === "east-asian" && styles.candle?.bar) {
      const t = readDesktopChartTheme();
      styles.candle.bar.upColor        = t.bearish;
      styles.candle.bar.downColor      = t.bullish;
      styles.candle.bar.upBorderColor  = t.bearish;
      styles.candle.bar.downBorderColor= t.bullish;
      styles.candle.bar.upWickColor    = t.bearish;
      styles.candle.bar.downWickColor  = t.bullish;
    }

    if (isReplayMode && !isStartLineLocked && interactive && isPrimaryChart) {
      const t = readDesktopChartTheme();
      styles.crosshair = {
        show: true,
        horizontal: { show: false },
        vertical: {
          show: true,
          line: { show: true, color: t.accent, style: "solid", size: 1 },
          text: { show: true, color: t.textPrimary, size: 11, family: "IBM Plex Mono", weight: "500", backgroundColor: t.overlay, borderColor: hexToRgba(t.accent, 0.6), borderSize: 1, paddingLeft: 6, paddingRight: 6, paddingTop: 3, paddingBottom: 3, borderRadius: 4 },
        },
      };
    }

    chart.setStyles(styles);
  }, [activeChartType, desktopMode, showOHLC, isReplayMode, isStartLineLocked, interactive, isPrimaryChart, chartAppearance.candleConvention]);

  useEffect(() => {
    const runtime = runtimeRef.current;
    if (!runtime) return;
    runtime.applyAppearance(chartAppearance);
  }, [chartAppearance]);

  // Apply y-axis scale mode (normal / log) to the candle pane
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;
    try {
      chart.setPaneOptions({
        id: 'candle_pane',
        axis: { name: chartAppearance.scaleMode === 'log' ? 'logarithm' : 'normal' },
      });
    } catch {
      // Ignore if chart not yet initialized or API unavailable
    }
  }, [chartAppearance.scaleMode]);

  // P0-A: Apply timezone preference to the live chart instance
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart || !timezone) return;
    try {
      chart.setTimezone(timezone);
    } catch {
      // Ignore if chart not yet initialized or API unavailable
    }
  }, [timezone]);

  useEffect(() => {
    const chart = chartRef.current;
    const runtime = runtimeRef.current;
    if (!chart || !runtime) return;
    chart.setPeriod(timeFrameToPeriod(activeTimeframe));
    runtime.setView({
      symbol,
      timeframe: activeTimeframe,
      chartType: activeChartType,
    });
  }, [activeChartType, activeTimeframe, symbol]);

  useEffect(() => {
    const runtime = runtimeRef.current;
    if (!runtime) return;
    runtime.setReplayState({
      isReplay,
      position,
      sessionState: replaySessionState,
      masterColumnsVersion: replayMasterColumnsVersion,
      sourceGranularitySeconds: replaySourceGranularitySeconds,
      contextCandles: replayContextCandles,
      contextGranularitySeconds: replayContextGranularitySeconds,
    });
  }, [
    isReplay,
    position,
    replayMasterColumnsVersion,
    replaySessionState,
    replaySourceGranularitySeconds,
    replayContextCandles,
    replayContextGranularitySeconds,
  ]);

  // --- Replay Position Line overlay (during play/pause) ---
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart || !isPrimaryChart) return;

    if (isReplay && cursorTimestamp != null) {
      // Skip override while the user is dragging — let klinecharts drive the line position,
      // same guard as the start-line overlay below.
      if (!isDraggingPositionLineRef.current) {
        const existing = chart.getOverlays({ name: REPLAY_POSITION_LINE_NAME });
        if (existing.length > 0 && existing[0]?.id) {
          chart.overrideOverlay({
            id: existing[0].id,
            points: [{ timestamp: cursorTimestamp, value: 0 }],
          });
        } else {
          chart.createOverlay({
            name: REPLAY_POSITION_LINE_NAME,
            points: [{ timestamp: cursorTimestamp, value: 0 }],
          });
        }
      }
    } else {
      chart.removeOverlay({ name: REPLAY_POSITION_LINE_NAME });
    }
  }, [isReplay, cursorTimestamp, isPrimaryChart]);

  // --- Replay Start Line overlay (only shown after locked) ---
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart || !isPrimaryChart || !interactive) return;

    if (isReplayMode && isStartLineLocked && startLineTimestamp != null) {
      // Skip the override while KlineCharts is actively dragging the line —
      // calling overrideOverlay mid-drag causes it to snap back to the old position.
      if (!isDraggingStartLineRef.current) {
        const existing = chart.getOverlays({ name: REPLAY_START_LINE_NAME });
        if (existing.length > 0 && existing[0]?.id) {
          chart.overrideOverlay({
            id: existing[0].id,
            points: [{ timestamp: startLineTimestamp, value: 0 }],
            styles: { line: { color: "#0EC4B4" } },
          });
        } else {
          chart.createOverlay({
            name: REPLAY_START_LINE_NAME,
            points: [{ timestamp: startLineTimestamp, value: 0 }],
            styles: { line: { color: "#0EC4B4" } },
          });
        }
      }
    } else {
      chart.removeOverlay({ name: REPLAY_START_LINE_NAME });
    }
  }, [isReplayMode, isStartLineLocked, startLineTimestamp, isPrimaryChart, interactive]);

  // Handle overlay drags — single consolidated effect.
  // Uses a RAF poll while the start-line is dragging so the date label updates live
  // without depending on whether klinecharts fires onOverlayDragging reliably.
  // The position line does NOT seek during drag — only on release — so backward drag
  // detection works cleanly and the override effect can't fight the drag.
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart || !interactive || !isPrimaryChart) return;

    let rafId: number | null = null;

    const pollStartLineTimestamp = () => {
      try {
        const overlays = chart.getOverlays({ name: REPLAY_START_LINE_NAME });
        const ts = (overlays[0] as any)?.points?.[0]?.timestamp;
        if (ts != null) useReplayStore.getState().setStartLineTimestamp(ts);
      } catch {}
      rafId = requestAnimationFrame(pollStartLineTimestamp);
    };

    // Belt-and-suspenders: also write on dragging events in case RAF misses frames
    const onOverlayDragging = (event: any) => {
      if (event?.overlay?.name === REPLAY_START_LINE_NAME) {
        const ts = event.overlay.points?.[0]?.timestamp;
        if (ts != null) useReplayStore.getState().setStartLineTimestamp(ts);
      }
    };

    const onOverlayDragStart = (event: any) => {
      if (event?.overlay?.name === REPLAY_START_LINE_NAME) {
        isDraggingStartLineRef.current = true;
        setIsDraggingStartLine(true);
        const store = useReplayStore.getState();
        if (store.isActive && store.sessionState === "playing") store.pause();
        rafId = requestAnimationFrame(pollStartLineTimestamp);
      }
      if (event?.overlay?.name === REPLAY_POSITION_LINE_NAME) {
        isDraggingPositionLineRef.current = true;
        setIsDraggingPositionLine(true);
        preDragCursorRef.current = useReplayStore.getState().cursorTimestamp;
        setPosLineRestartPrompt(null);
        const store = useReplayStore.getState();
        if (store.isActive && store.sessionState === "playing") store.pause();
      }
    };

    const onOverlayDragEnd = (event: any) => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }

      if (event?.overlay?.name === REPLAY_START_LINE_NAME) {
        isDraggingStartLineRef.current = false;
        setIsDraggingStartLine(false);
        // Prefer event.overlay.points (final dragged position); getOverlays() may return stale data
        const ts = event.overlay.points?.[0]?.timestamp
          ?? (chart.getOverlays({ name: REPLAY_START_LINE_NAME })[0] as any)?.points?.[0]?.timestamp;
        if (ts != null) useReplayStore.getState().setStartLineTimestamp(ts);
      }

      if (event?.overlay?.name === REPLAY_POSITION_LINE_NAME) {
        isDraggingPositionLineRef.current = false;
        setIsDraggingPositionLine(false);
        const ts = event.overlay.points?.[0]?.timestamp
          ?? (chart.getOverlays({ name: REPLAY_POSITION_LINE_NAME })[0] as any)?.points?.[0]?.timestamp;
        if (ts != null) {
          const preTs = preDragCursorRef.current;
          if (preTs !== null && ts < preTs) {
            // Dragged backward — show restart prompt without seeking; line stays where dropped.
            // Cancel/timeout at the auto-dismiss effect will seek back to preDragCursorRef.
            setPosLineRestartPrompt(ts);
          } else {
            useReplayStore.getState().seekToTimestamp(ts);
          }
        }
      }
    };

    // Prevent right-click from permanently deleting the position line during active replay
    const onOverlayRemove = (event: any) => {
      if (event?.overlay?.name === REPLAY_POSITION_LINE_NAME) {
        const store = useReplayStore.getState();
        if (store.isActive && store.cursorTimestamp != null) {
          const ts = store.cursorTimestamp;
          setTimeout(() => {
            chart.createOverlay({
              name: REPLAY_POSITION_LINE_NAME,
              points: [{ timestamp: ts, value: 0 }],
            });
          }, 0);
        }
      }
    };

    (chart as any).subscribeAction("onOverlayDragging", onOverlayDragging);
    (chart as any).subscribeAction("onOverlayDragStart", onOverlayDragStart);
    (chart as any).subscribeAction("onOverlayDragEnd", onOverlayDragEnd);
    (chart as any).subscribeAction("onOverlayRemove", onOverlayRemove);
    return () => {
      if (rafId !== null) cancelAnimationFrame(rafId);
      (chart as any).unsubscribeAction("onOverlayDragging", onOverlayDragging);
      (chart as any).unsubscribeAction("onOverlayDragStart", onOverlayDragStart);
      (chart as any).unsubscribeAction("onOverlayDragEnd", onOverlayDragEnd);
      (chart as any).unsubscribeAction("onOverlayRemove", onOverlayRemove);
    };
  }, [interactive, isPrimaryChart]);

  // --- Click to place the start line overlay during replay selection ---
  useEffect(() => {
    if (!isReplayMode || isStartLineLocked) return;
    const container = containerRef.current;
    const chart = chartRef.current;
    if (!container || !chart || !isPrimaryChart || !interactive) return;

    let latestTimestamp: number | null = null;
    let downX = 0;
    let downY = 0;
    const MOVE_THRESHOLD = 5;

    const handleCrosshairChange = (data?: unknown) => {
      const crosshair = data as { paneId?: string; timestamp?: number } | undefined;
      if (crosshair?.timestamp != null) {
        latestTimestamp = crosshair.timestamp;
      }
    };

    const handlePointerDown = (e: PointerEvent) => {
      if (e.pointerType === "mouse" && e.button !== 0) return;
      downX = e.clientX;
      downY = e.clientY;
    };

    const handlePointerUp = (e: PointerEvent) => {
      if (e.pointerType === "mouse" && e.button !== 0) return;
      const dx = Math.abs(e.clientX - downX);
      const dy = Math.abs(e.clientY - downY);
      if (dx > MOVE_THRESHOLD || dy > MOVE_THRESHOLD) return; // was a drag/pan

      // Resolve timestamp: prefer crosshair, fallback to convertFromPixel
      let ts = latestTimestamp;
      if (ts == null) {
        const rect = container.getBoundingClientRect();
        const result = chart.convertFromPixel([{ x: e.clientX - rect.left }], { paneId: "candle_pane" }) as Array<{ timestamp?: number }> | undefined;
        ts = Array.isArray(result) ? result[0]?.timestamp ?? null : null;
      }
      if (ts == null) return;

      // Suppress placement in the no-data zone
      const oldestTs = useChartStore.getState().oldestEpoch;
      if (oldestTs != null && ts < oldestTs) return;

      chart.createOverlay({
        name: REPLAY_START_LINE_NAME,
        points: [{ timestamp: ts, value: 0 }],
      });

      useReplayStore.getState().setStartLineTimestamp(ts);
      useReplayStore.getState().lockStartLine();
    };

    chart.subscribeAction("onCrosshairChange", handleCrosshairChange);
    container.addEventListener("pointerdown", handlePointerDown);
    container.addEventListener("pointerup", handlePointerUp);
    return () => {
      chart.unsubscribeAction("onCrosshairChange", handleCrosshairChange);
      container.removeEventListener("pointerdown", handlePointerDown);
      container.removeEventListener("pointerup", handlePointerUp);
    };
  }, [isReplayMode, isStartLineLocked, isPrimaryChart, interactive]);

  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;
    syncIndicators(chart, interactive ? indicators : []);
  }, [indicators, interactive, syncIndicators]);

  useEffect(() => {
    if (!interactive) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (
        event.target instanceof HTMLInputElement ||
        event.target instanceof HTMLTextAreaElement
      ) {
        return;
      }
      if (event.key === "Escape" && activeTool) {
        event.preventDefault();
        cancelPendingDrawings();
        setActiveTool(null);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [activeTool, interactive, setActiveTool]);

  // Handle crosshair hide to clear OHLC data when mouse leaves the chart
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart || !interactive || !isPrimaryChart) return;

    const handleCrosshairChange = (data?: unknown) => {
      const crosshairData = data as { paneId?: string } | undefined;
      // If crosshair is hidden (no paneId), clear OHLC data
      if (!crosshairData?.paneId) {
        useChartStore.getState().setOHLC(null);
      }
    };

    chart.subscribeAction("onCrosshairChange", handleCrosshairChange);

    return () => {
      chart.unsubscribeAction("onCrosshairChange", handleCrosshairChange);
    };
  }, [interactive, isPrimaryChart]);

  // Reset drawingsRestoredRef when a new file is loaded (symbol changes)
  useEffect(() => {
    drawingsRestoredRef.current = false;
  }, [symbol]);

  useEffect(() => {
    if (!interactive || drawingsRestoredRef.current || isReplay || !hasData) return;

    const session = useSessionStore.getState();
    const drawings =
      session.pendingRestoreDrawings ??
      (session.lastSnapshot?.drawings?.length ? session.lastSnapshot.drawings : null);
    if (!drawings) return;

    const timer = setTimeout(() => {
      const chart = chartRef.current;
      if (!chart || chart.getDataList().length === 0) return;

      drawingsRestoredRef.current = true;
      const count = deserializeOverlays(drawings);
      if (count > 0) {
        console.log(`[LYTICK] Session restore: ${count} drawing(s) restored`);
      }
      // Clear pending queue after restore
      useSessionStore.getState().setPendingRestoreDrawings(null);
    }, 300);

    return () => clearTimeout(timer);
  }, [hasData, interactive, isReplay, symbol]);

  const buildSnapshot = useCallback((): import("@/stores/sessionStore").SessionSnapshot => {
    const cs = useChartStore.getState();
    return {
      symbol: cs.symbol,
      symbolName: cs.activeSymbolName,
      timeframe: cs.timeframe,
      chartType: cs.chartType,
      indicators: cs.indicators,
      drawings: serializeOverlays(),
      drawingColor: useDrawingStore.getState().drawingColor,
      savedAt: Date.now(),
    };
  }, []);

  const saveWithHash = useCallback(() => {
    const snapshot = buildSnapshot();
    useSessionStore.getState().saveSession(snapshot);
    const hash = getCurrentCsvHash();
    if (hash) saveDrawingsForHash(hash, snapshot.drawings);
  }, [buildSnapshot]);

  useEffect(() => {
    if (!interactive || isReplay) return;
    saveWithHash();
  }, [saveWithHash, chartType, indicators, interactive, isReplay, symbol, timeframe]);

  useEffect(() => {
    if (!interactive || isReplay) return;
    let prevLength = useDrawingStore.getState().drawings.length;
    const unsubscribe = useDrawingStore.subscribe((state) => {
      const nextLength = state.drawings.length;
      if (nextLength !== prevLength) {
        prevLength = nextLength;
        saveWithHash();
      }
    });
    return unsubscribe;
  }, [saveWithHash, interactive, isReplay]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      if (!interactive) return;
      if (useReplayStore.getState().isActive) return;
      const snapshot = buildSnapshot();
      useSessionStore.getState().saveSessionNow(snapshot);
      const hash = getCurrentCsvHash();
      if (hash) saveDrawingsForHash(hash, snapshot.drawings);
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [buildSnapshot, interactive]);

  useEffect(() => {
    const chart = chartRef.current;
    if (!chart || !interactive || !isPrimaryChart) return;

    const handleZoom = () => {
      clearTimeout(zoomBadgeTimerRef.current);
      try {
        const range = (chart as any).getVisibleRange() as { from: number; to: number } | null;
        if (range) {
          const count = Math.round(range.to - range.from);
          if (count > 0) {
            setZoomBadge(`${count} candle${count !== 1 ? "s" : ""}`);
            zoomBadgeTimerRef.current = setTimeout(() => setZoomBadge(null), 900);
          }
        }
      } catch {
        // getVisibleRange may not exist on all klinecharts builds; ignore silently
      }
    };

    (chart as any).subscribeAction("onZoomEnd", handleZoom);
    return () => {
      clearTimeout(zoomBadgeTimerRef.current);
      (chart as any).unsubscribeAction("onZoomEnd", handleZoom);
    };
  // chartRef.current is stable across renders; re-run when interactive/isPrimaryChart change
  }, [interactive, isPrimaryChart]);

  const showConnecting = false;
  const showEmpty = !isReplay && !hasData;
  const cursorClass = interactive && isPrimaryChart && activeTool
    ? "cursor-crosshair"
    : interactive && isPrimaryChart && isReplayMode && !isStartLineLocked
      ? "cursor-pointer"
    : interactive && isPrimaryChart && isReplayMode && isStartLineLocked && isDraggingStartLine
      ? "cursor-grabbing"
    : interactive && isPrimaryChart && isReplayMode && isStartLineLocked && isNearStartLine
      ? "cursor-grab"
    : interactive && isPrimaryChart && isReplay && isDraggingPositionLine
      ? "cursor-grabbing"
    : interactive && isPrimaryChart && isReplay && isNearPositionLine
      ? "cursor-grab"
      : "";

  const showPlacementMode = interactive && isPrimaryChart && isReplayMode && !isStartLineLocked;

  return (
    <div
      id="onboarding-chart"
      className={cn(
        "flex-1 min-h-0 relative",
        cursorClass,
      )}
      data-chart-id={chartId}
    >
      <div ref={containerRef} className="w-full h-full" style={{ touchAction: activeTool ? 'none' : 'auto' }} />

      {showPlacementMode && noDataBoundaryPx != null && noDataBoundaryPx > 0 && (
        <NoDataOverlay boundaryPx={noDataBoundaryPx} />
      )}

      {isReplay && blindMode && blindCurtainPx != null && (
        <div
          className="absolute inset-y-0 pointer-events-none z-[4]"
          style={{ left: blindCurtainPx }}
        >
          <div
            className="absolute inset-0"
            style={{ backgroundColor: "color-mix(in srgb, var(--bg-base, #000) 70%, transparent)" }}
          />
          <div
            className="absolute inset-y-0 left-0 w-px"
            style={{ backgroundColor: "var(--accent-primary)", opacity: 0.5 }}
          />
        </div>
      )}

      {showConnecting && (
        <>
          <ChartSkeleton />
          <div className="absolute inset-0 z-[5] flex items-end justify-center pb-8 pointer-events-none">
            <span className="text-xs text-muted-foreground/60 font-mono">Loading candles…</span>
          </div>
        </>
      )}
      {showEmpty && (
        <div className="absolute inset-0 z-[5] flex flex-col items-center justify-center gap-2 pointer-events-none">
          <span className="text-sm font-medium" style={{ color: "var(--text-secondary)" }}>No data available</span>
          <span className="text-xs" style={{ color: "var(--text-muted)" }}>Select a symbol to begin</span>
        </div>
      )}

      {zoomBadge && interactive && isPrimaryChart && (
        <div className="absolute top-3 right-3 z-10 pointer-events-none">
          <span
            className="text-xxs font-medium px-2 py-0.5 rounded-full backdrop-blur-sm select-none tabular-nums"
            style={{
              backgroundColor: "var(--bg-elevated)",
              color: "var(--text-secondary)",
              border: "1px solid var(--border-default)",
              fontFamily: "var(--font-mono)",
            }}
          >
            {zoomBadge}
          </span>
        </div>
      )}



      {interactive && isPrimaryChart && !isReplay && isUserScrolled && hasData && (
        <button
          type="button"
          aria-label="Return to live edge"
          onClick={jumpToLive}
          className="pointer-events-auto absolute z-20 flex items-center justify-center h-7 w-7 rounded-full opacity-40 hover:opacity-90 transition-opacity"
          style={{
            bottom: "calc(0.5in + 1.5rem)",
            right: "calc(1in + 3.75rem)",
            backgroundColor: "color-mix(in srgb, var(--accent-primary) 12%, transparent)",
            color: "var(--accent-primary)",
            border: "1px solid color-mix(in srgb, var(--accent-primary) 30%, transparent)",
          }}
        >
          <ChevronsRight className="h-4 w-4" />
        </button>
      )}
      {interactive && isPrimaryChart && isReplay ? (
        <div className="pointer-events-none absolute bottom-3 right-3 z-10">
          <ReplayBufferIndicator />
        </div>
      ) : null}
      {interactive && isPrimaryChart ? <ObjectInspector /> : null}
      {interactive && isPrimaryChart ? <TextAnnotationEditor /> : null}
      {interactive && isPrimaryChart ? (
        <DrawingContextMenu containerRef={containerRef} chartRef={chartRef} />
      ) : null}
      {interactive && isPrimaryChart ? (
        <ReplayContextMenu containerRef={containerRef} chartRef={chartRef} />
      ) : null}

      {/* Chart Template Save Dialog (Ctrl+S) */}
      {interactive && isPrimaryChart && (
        <ChartTemplateSaveDialog
          open={templateDialogOpen}
          onClose={() => setTemplateDialogOpen(false)}
          chartRef={chartRef as React.RefObject<unknown>}
        />
      )}

      {/* Drawing warning when switching to a chart type that hides drawings */}
      {drawingWarning && (
        <DrawingWarningDialog
          open={true}
          targetChartType={drawingWarning.targetType}
          drawingCount={drawings.length}
          onConfirm={() => {
            setChartType(drawingWarning.targetType);
            setDrawingWarning(null);
          }}
          onCancel={() => setDrawingWarning(null)}
        />
      )}

      {/* Restart prompt — shown when position line is dragged backward during replay */}
      {interactive && isPrimaryChart && posLineRestartPrompt !== null && (
        <div className="absolute bottom-20 left-1/2 -translate-x-1/2 z-30 animate-in slide-in-from-bottom-2 duration-200 pointer-events-auto">
          <div
            className="flex items-center gap-3 rounded-xl px-4 py-2.5 shadow-[0_4px_24px_rgba(0,0,0,0.5)] whitespace-nowrap"
            style={{
              backgroundColor: "var(--bg-elevated)",
              border: "1px solid var(--lyt-border-default)",
            }}
          >
            <span className="text-xs text-foreground">
              Restart from{" "}
              <span className="font-semibold text-golden font-mono-tabular">
                {formatRestartDate(posLineRestartPrompt)}
              </span>
              ?
            </span>
            <button
              type="button"
              onClick={() => {
                const store = useReplayStore.getState();
                store.startPreflight(posLineRestartPrompt, store.endDate ?? Date.now());
                setPosLineRestartPrompt(null);
              }}
              className="h-6 px-3 rounded-full bg-golden text-background text-xs font-bold hover:opacity-90 transition-opacity shrink-0"
            >
              Restart
            </button>
            <button
              type="button"
              onClick={() => {
                if (preDragCursorRef.current !== null) {
                  useReplayStore.getState().seekToTimestamp(preDragCursorRef.current);
                }
                setPosLineRestartPrompt(null);
              }}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors shrink-0"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// NoDataOverlay — cursor-following tooltip over the unavailable data zone
// ---------------------------------------------------------------------------

function NoDataOverlay({ boundaryPx }: { boundaryPx: number }) {
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setPos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
  }, []);

  const handleMouseLeave = useCallback(() => {
    setPos(null);
  }, []);

  return (
    <div
      className="absolute top-0 left-0 z-[5] cursor-not-allowed"
      style={{
        width: `${boundaryPx}px`,
        height: "100%",
        backgroundColor: "rgba(0, 0, 0, 0.18)",
      }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      {pos != null && (
        <div
          className="absolute pointer-events-none whitespace-nowrap z-50"
          style={{
            left: pos.x + 12,
            top: pos.y - 28,
          }}
        >
          <div
            className="rounded px-2 py-1 text-xs leading-tight text-white"
            style={{ backgroundColor: "rgba(0, 0, 0, 0.8)" }}
          >
            No data — select a more recent point
          </div>
        </div>
      )}
    </div>
  );
}
